import { Content, ContentWrapper, DropdownGlobalStyles, Heading, PendingStepsCount, StepWrapper } from "./styles";
import { getLastOnboardingSteps } from "./redux/actions";
import React, { useEffect, useState, useCallback, useRef } from "react";
import OnBoardingWrapper from "./components/OnBoardingWrapper";
import { stepsData } from "./components/StepData";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useNavigate } from "react-router-dom";
import { updateBusinessId } from "../Login/usersSlice";
import { updateBusinessType } from "./redux/slice";
import ProgressSlider from "./components/ProgressSlider";
import { motion, useAnimation } from "framer-motion";

const Onboarding: React.FC = () => {
  const dispatch = useAppDispatch();
  const { businessId, userObj: { accountUser: { businessName}} } = useAppSelector((state) => state.users);
  const navigate = useNavigate()
  const greeting = "Welcome to Klasha!";
  const copy = "Let’s help you setup your account.";
  const [currentStep, setCurrentStep] = useState(1);

  const handleChangeStep = (index: number) => {
    if (index >= 0 && index < stepsData.length) {
      setCurrentStep(index);
    }
  };

  const renderStepContent = () => {
    const currentStepData = stepsData[currentStep];
    const StepComponent = currentStepData?.component as any;

    if (StepComponent) {
      return (
        <StepComponent
          handleNext={() => handleChangeStep(currentStep + 1)}
          handlePrevious={() => handleChangeStep(currentStep - 1)}
        />
      );
    }

    return null;
  };

  const stepsItems = stepsData.map((step) => ({
    title: step.title,
    description: step.description,
  }));

  const fetchNextStep = useCallback(async () => {
    const resultAction = await dispatch(getLastOnboardingSteps({ businessId }));

    if (getLastOnboardingSteps.fulfilled.match(resultAction)) {
      const { nextStep, business } = resultAction.payload;
      dispatch(updateBusinessId(business?.id));
      dispatch(updateBusinessType(business?.businessType));
      if (nextStep === 'DONE' || business?.approved) {
        navigate('/dashboard');
        return;
      }

      const stepNumber = parseInt(nextStep.replace("STEP", ""), 10);
      setCurrentStep((prevStep) =>
        prevStep !== stepNumber ? stepNumber : prevStep
      );
    }
  }, [dispatch, businessId, navigate]);
  
  useEffect(() => {
    fetchNextStep();
  }, [fetchNextStep]);

  const MotionContentWrapper = motion.custom(ContentWrapper);

  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();

  useEffect(() => {
    const element = contentWrapperRef.current;
    if (element) {
      const isScrollable = element.scrollHeight > element.clientHeight;
      
      if (isScrollable) {
        controls.start({
          y: [0, -15, 0],
          transition: { 
            type: "keyframes", 
            duration: 1,
            ease: "easeInOut",
            delay: 1,
          },
        });
      }
    }
  }, [contentWrapperRef, controls]);

  return (
    <OnBoardingWrapper>
      <DropdownGlobalStyles />
      <Heading>
        <span>Hello {businessName}</span>, {greeting}
      </Heading>
      <Heading>{copy}</Heading>
      <Content>
        <section>
          <StepWrapper
            direction="vertical"
            current={currentStep}
            items={stepsItems}
          />
          <ProgressSlider currentStep={currentStep} totalSteps={8} />
        </section>

        <MotionContentWrapper ref={contentWrapperRef} animate={controls}>
          {renderStepContent()}
        </MotionContentWrapper>
        <PendingStepsCount>{8 - currentStep} step{currentStep < 7 && 's'} left to complete</PendingStepsCount>
      </Content>
    </OnBoardingWrapper>
  );
};

export default Onboarding;
